import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ModifyDashboardView } from '@futrli/views/views/reports/ModifyDashboardView/ModifyDashboardView';
import { useApiView } from '@futrli/views/hooks/useApiView';

interface PublishDashboardDialogProps {
  dashboardId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const PublishDashboardDialog = ({ dashboardId, isOpen, onClose }: PublishDashboardDialogProps) => {
  const intl = useIntl();
  const { workspaceId = '', organisationId = '' } = useParams();

  const view = useApiView(
    useMemo(() => new ModifyDashboardView(workspaceId, organisationId, dashboardId), [
      workspaceId,
      organisationId,
      dashboardId
    ])
  );

  // Need to keep this in useState so that the success text does not update after the publish/unpublish action has completed, as the dashboard status will have updated in the view
  const [isPublishing] = useState(view.state.dashboard?.status === 'draft');

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isUpdating,
    error: view.state.errorUpdating,
    onAction: isPublishing ? () => view.publishDashboard() : () => view.unpublishDashboard(),
    onClose
  });

  const text = useMemo(() => {
    if (isPublishing) {
      return {
        title: intl.formatMessage({ id: 'common.publish', defaultMessage: 'Publish' }),
        description: intl.formatMessage({
          id: 'predict.dashboards.publish_dashboard_description',
          defaultMessage: 'Publishing this dashboard will make it available for all users to see'
        }),
        loadingMessage: intl.formatMessage({
          id: 'predict.dashboards.publishing_dashboard',
          defaultMessage: 'Publishing dashboard...'
        }),
        successMessage: intl.formatMessage({
          id: 'predict.dashboards.dashboard_published',
          defaultMessage: 'Dashboard published'
        })
      };
    }
    return {
      title: intl.formatMessage({ id: 'common.unpublish', defaultMessage: 'Unpublish' }),
      description: intl.formatMessage({
        id: 'predict.dashboards.unpublish_dashboard_description',
        defaultMessage: 'Unpublishing this dashboard will put it back into a draft state'
      }),
      loadingMessage: intl.formatMessage({
        id: 'predict.dashboards.unpublishing_dashboard',
        defaultMessage: 'Unpublishing dashboard...'
      }),
      successMessage: intl.formatMessage({
        id: 'predict.dashboards.dashboard_unpublished',
        defaultMessage: 'Dashboard unpublished'
      })
    };
  }, [intl, isPublishing]);

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleAction}
      screen={screen}
      title={text.title}
      subtitle={view.state.dashboard?.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={text.description}
      loadingMessage={text.loadingMessage}
      successMessage={text.successMessage}
      errorMessage={view.state.errorUpdating?.message}
      dataTestId="publish-dashboard-dialog"
    />
  );
};
